import { CheckCircle2, Info, XCircle } from 'lucide-react';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { ToastContext, type ToastApi } from './toast-context';
import { cn } from '@/lib/utils';

type Tone = 'success' | 'error' | 'info';
interface Toast {
  id: number;
  title: string;
  description?: string;
  tone: Tone;
}
let seq = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((t: Omit<Toast, 'id'>) => {
    const id = ++seq;
    setToasts((prev) => [...prev, { ...t, id }]);
    setTimeout(
      () => setToasts((prev) => prev.filter((x) => x.id !== id)),
      t.tone === 'error' ? 7000 : 4000,
    );
  }, []);
  const api = useMemo<ToastApi>(
    () => ({
      push,
      success: (title, description) => push({ title, description, tone: 'success' }),
      error: (title, description) => push({ title, description, tone: 'error' }),
    }),
    [push],
  );
  const icons = { success: CheckCircle2, error: XCircle, info: Info };
  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-80 flex-col gap-2"
        role="status"
        aria-live="polite"
      >
        {toasts.map((t) => {
          const Icon = icons[t.tone];
          return (
            <div
              key={t.id}
              className={cn(
                'bg-card animate-in slide-in-from-bottom-2 pointer-events-auto flex gap-3 rounded-lg border p-3 text-sm shadow-lg',
                t.tone === 'success' && 'border-emerald-200',
                t.tone === 'error' && 'border-red-200',
              )}
            >
              <Icon
                className={cn(
                  'mt-0.5 h-4 w-4 shrink-0',
                  t.tone === 'success'
                    ? 'text-emerald-600'
                    : t.tone === 'error'
                      ? 'text-red-600'
                      : 'text-primary',
                )}
              />
              <div>
                <p className="font-medium">{t.title}</p>
                {t.description && <p className="text-muted-foreground">{t.description}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
